import { useState, MouseEvent } from "react";
import type { ReactNode } from "react";
import {
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import KeyboardArrowDownRoundedIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import {
  crmStatuses,
  crmStatusLabels,
  getContactCrmSides,
  getCrmSubStatusDefinitionsForSides,
  findCrmSubStatusDefinition
} from "../../../../@types/sharedSchema";
import type { CrmStatus, CrmSubStatusDefinition } from "../../../../@types/sharedSchema";
import { CrmStatusIcon, CrmHotFireIcon } from "../../contacts/crm/CrmIcons";
import type { CrmContactRow } from "../../../../@types/webotServer";

/**
 * The one-click status control — the component this page exists for.
 *
 * The shipped CRM status spec requires that every contact surface let the user read AND
 * change status, sub-status and 🔥 with a single click, inline, without opening the edit
 * modal. The old contacts table does not, which is why this exists.
 *
 * THE UNSET STATE IS THE DESIGN, NOT AN EDGE CASE. Measured across the pilot org, only
 * 2,507 of 47,872 contacts (5%) carry a status — and the per-agent spread is bimodal:
 * one agent has 1,044, another has 6 out of 25,066. So a dashed "+ סטטוס" invitation is
 * what most rows render most of the time, and it is styled as an invitation rather than
 * as a defect. (The old table renders a bare empty chip here — a grey circle with no
 * label, which reads as broken software.)
 */

/** How the sub-status shares the pill with its parent status — see the `subStatus` prop. */
export type CrmStatusSubDisplay = "append" | "stack" | "replace" | "hide";

type Props = {
  contact: CrmContactRow;
  onSetStatus: (status: CrmStatus | null, subStatus?: string | null) => void;
  onToggleHot: () => void;
  size?: "medium" | "small";
  /**
   * MINIMUM TOUCH HEIGHT, in px — a bigger HIT AREA around the same-sized pill.
   *
   * The mobile card used to get its 44px target by reaching in from outside and forcing
   * `min-height: 44` on this component's `ButtonBase` through a descendant selector. That
   * inflated the pill VISUALLY: a 44px-tall coloured slab, on the row where the whole
   * complaint was crowding, and it made the pill the tallest thing on the card while
   * saying no more than a 22px one does.
   *
   * So the pill keeps its `size` height and grows an invisible `::after` instead. Three
   * reasons it is a pseudo-element and not padding:
   *
   *  - Padding is inside the button's own background, so it would paint the pill 44px tall
   *    again — the exact thing being fixed.
   *  - A padded WRAPPER cannot work either: the padding belongs to the wrapper, not to the
   *    button, so a tap there does nothing (and on this card the wrapper `stopPropagation`s,
   *    so it would swallow the tap that would otherwise have opened the contact).
   *  - `ButtonBase` is `position: relative` and — verified against the installed
   *    5.0.0-beta.4 — carries NO `overflow: hidden` of its own; the ripple is clipped by
   *    `TouchRipple`'s own container. So an overflowing pseudo-element is safe here and
   *    costs nothing in layout, which is the point: the row's height is unchanged.
   *
   * The extension is BIASED UPWARDS (all but 8px of it). Downwards, at the pill's inline
   * position, is the next line's inline-end — the record's age, inert text. Upwards is the
   * row's own top padding. Neither holds a control, and the smaller half is the one aimed
   * at real content.
   */
  minTouchTarget?: number;
  /**
   * OPT-IN width bound, in px. Absent = the pill is content-sized, which is what the
   * mobile card wants (it wraps, so a long pill costs a line, not a clipped row).
   *
   * The bound is opt-in rather than a constant inside this component because the pill is
   * the only `whiteSpace: nowrap`, `flexShrink: 0`, CONTENT-SIZED child of the desktop
   * row — every other child is a fixed width — so a long `בטיפול · פגישת הערכת שווי`
   * (~201px against an unset pill's 57px) came straight out of the identity block, which
   * is the one child that can shrink. `crmTablePlan.ts` reserves exactly this number and
   * lays the rest of the table out around it.
   */
  maxWidth?: number;
  /**
   * HOW THE SUB-STATUS SHARES THE PILL WITH ITS PARENT STATUS — one axis, four answers,
   * one per surface. It replaces the `showSubStatus` / `stackSubStatus` boolean pair,
   * which described the same axis in two flags whose precedence was never stated.
   *
   * Sub-statuses exist only under בטיפול (`getCrmSubStatusDefinitionsForSides`), so every
   * mode below is `label` alone for the other five statuses and for the ~95% of contacts
   * carrying no status at all.
   *
   *   "append"  — `בטיפול · בהצגת נכסים`. The default, and the panel's: it has the width.
   *   "stack"   — the sub on its own line under the status word, in the status colour.
   *               The DESKTOP TABLE's, and it replaced a concession rather than adding
   *               one: the old row DROPPED the sub-status entirely at its first narrow
   *               step, so the refinement a realtor actually works by was the first thing
   *               to go and was recoverable only by hovering. Its own line can never be
   *               dropped for width — it costs ROW HEIGHT once, for every row, instead of
   *               costing information on the narrow ones, and that is why
   *               `CRM_TABLE_ROW_HEIGHT` is 60 rather than the 44 one line would allow.
   *   "replace" — the sub INSTEAD of the status: `בהצגת נכסים`, with the בטיפול icon and
   *               the בטיפול colour unchanged. The MOBILE CARD's, asked for directly
   *               (2026-08-26): "when in status בטיפול, on mobile, show the sub-status if
   *               set — and since there is not enough space, show the sub-status instead
   *               of בטיפול, using the same בטיפול icon". It is the right trade at 360px
   *               because the parent is RECOVERABLE and the child is not: the icon and
   *               the colour already say בטיפול, so the word is the redundant half.
   *   "hide"    — the status word alone. What the card did before "replace", kept because
   *               it is still the correct answer for any surface too narrow for either.
   *
   * ⚠️ EVERY MODE KEEPS THE FULL TEXT IN `title` AND IN THE ACCESSIBLE NAME (`fullLabel`),
   * so no mode can actually lose the parent status — which is what makes "replace" safe.
   */
  subStatus?: CrmStatusSubDisplay;
  /**
   * THE VIEWER MAY NOT WRITE THIS CONTACT — a colleague's row on the agency tab. See
   * `crmRowAccess` for the rule and the three write channels this closes.
   *
   * The pill still RENDERS its status, without the caret, the hover, the hit-area
   * overhang or the menu: the status is information, and reading a colleague's pipeline
   * is the whole point of the agency tab. What goes is the affordance.
   *
   * The UNSET state renders NOTHING at all. That state is a dashed `+ סטטוס`
   * INVITATION — its entire job is to say "click me" — and it is what ~95% of rows show,
   * so leaving it in place read-only would fill somebody else's book with invitations
   * that do nothing. `disabled` fails the same way, just greyer.
   */
  readOnly?: boolean;
};

const STATUS_COLOR: Record<CrmStatus, string> = {
  new: "info",
  in_progress: "primary",
  paused: "warning",
  won: "success",
  lost: "error",
  irrelevant: "default"
};

export default function CrmStatusControl({
  contact,
  onSetStatus,
  onToggleHot,
  size = "medium",
  minTouchTarget,
  maxWidth,
  subStatus: subStatusDisplay = "append",
  readOnly = false
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const status = (contact.crmStatus ?? null) as CrmStatus | null;
  const subStatus = contact.crmSubStatus ?? null;
  const subDefinition = subStatus ? findCrmSubStatusDefinition(subStatus) : undefined;

  // Offer only the sub-statuses that match this contact's side(s): a buyer should not
  // be offered "חתימת בלעדיות". With no customer type set, both sides are offered.
  const sides = getContactCrmSides({ customerTypes: contact.customerTypes } as never);
  const subDefinitions = getCrmSubStatusDefinitionsForSides(sides.length ? sides : (["buyer", "seller"] as never));

  const open = (event: MouseEvent<HTMLElement>) => {
    // The row itself opens the contact detail — a status click must not do both.
    event.stopPropagation();
    if (isMobile) setDrawerOpen(true);
    else setAnchorEl(event.currentTarget);
  };

  const close = () => {
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  const choose = (event: MouseEvent, next: CrmStatus | null, sub?: string | null) => {
    event.stopPropagation();
    onSetStatus(next, sub ?? null);
    close();
  };

  const label = status ? crmStatusLabels[status] : null;
  const colorKey = status ? STATUS_COLOR[status] : "default";
  const palette = (theme.palette as unknown as Record<string, { main: string; lighter?: string; darker?: string }>)[colorKey];

  const height = size === "small" ? 22 : 26;

  /** See `minTouchTarget`. Zero-cost and absent entirely on every desktop caller. */
  const touchOverhang = Math.max(0, (minTouchTarget ?? 0) - height);
  const touchBelow = Math.min(8, touchOverhang);
  const hitAreaSx = touchOverhang
    ? {
        "&::after": {
          content: '""',
          position: "absolute",
          top: `${-(touchOverhang - touchBelow)}px`,
          bottom: `${-touchBelow}px`,
          insetInlineStart: 0,
          insetInlineEnd: 0
        }
      }
    : {};

  // What the status IS, always — the `title` and the accessible name read it whether or
  // not the sub-status label is on screen, so a row that dropped the label to fit is
  // still recoverable with a hover or a screen reader.
  const fullLabel = label ? `${label}${subDefinition ? ` · ${subDefinition.label}` : ""}` : "";

  /**
   * Two lines, or one? Only ever two when there is genuinely a second line to draw —
   * see the `subStatus` prop's `"stack"` mode. Measured: 2,507 of 47,872 contacts carry a status at all, and
   * only some of those carry a refinement, so the one-line form remains what this
   * control renders on nearly every row.
   */
  const stacked = Boolean(subStatusDisplay === "stack" && subDefinition);

  /**
   * The pill's VISIBLE text. `fullLabel` above stays the whole truth for `title` and the
   * accessible name whatever this returns — see the prop.
   */
  const pillText =
    subDefinition && subStatusDisplay === "replace"
      ? subDefinition.label
      : `${label ?? ""}${subDefinition && subStatusDisplay === "append" ? ` · ${subDefinition.label}` : ""}`;

  /**
   * READ-ONLY: the pill without the affordance — see the prop.
   *
   * Returned BEFORE the menu is built, so a colleague's row constructs neither the item
   * array nor the Drawer/Menu. On the agency tab that is most of the fifty rows on screen,
   * each of which would otherwise assemble ~12 MenuItems it can never show.
   *
   * The stacked form is deliberately not reproduced: the two-line pill exists so the
   * desktop table never has to DROP a sub-status for width, and with the caret gone the
   * one-line form has room for `בטיפול · פגישת הערכת שווי` anyway. One shape, and the
   * `title` still carries the full text either way.
   */
  if (readOnly) {
    if (!status) return null;
    return (
      <Box
        title={fullLabel}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height,
          borderRadius: 1,
          px: 0.75,
          bgcolor: palette?.lighter ?? "grey.200",
          color: palette?.darker ?? "text.primary",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: "nowrap",
          maxWidth: maxWidth ?? "100%",
          minWidth: 0
        }}
      >
        <CrmStatusIcon iconKey={status} sx={{ fontSize: 14, flexShrink: 0 }} />
        <Box component="span" sx={{ mx: 0.25, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {pillText}
        </Box>
      </Box>
    );
  }

  const chip = status ? (
    <ButtonBase
      onClick={open}
      title={fullLabel}
      aria-label={`שנה סטטוס — כרגע ${fullLabel}`}
      sx={{
        // `stretch` + `auto` height in the stacked form so the two lines share the
        // pill's width (the widest of them sets it) rather than each shrinking to fit.
        height: stacked ? "auto" : height,
        minHeight: height,
        flexDirection: stacked ? "column" : "row",
        alignItems: stacked ? "stretch" : "center",
        borderRadius: 1,
        px: 0.75,
        py: stacked ? "3px" : 0,
        gap: stacked ? "1px" : 0.25,
        bgcolor: palette?.lighter ?? "grey.200",
        color: palette?.darker ?? "text.primary",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: "nowrap",
        // `100%` when unbounded so the pill can never paint outside a wrapping container
        // either; the ellipsis below is inert until something actually constrains it.
        maxWidth: maxWidth ?? "100%",
        minWidth: 0,
        "&:hover": { filter: "brightness(0.96)" },
        ...hitAreaSx
      }}
    >
      {stacked ? (
        <>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
            <CrmStatusIcon iconKey={status} sx={{ fontSize: 14, flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                mx: 0.25,
                minWidth: 0,
                flexGrow: 1,
                textAlign: "start",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {label}
            </Box>
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, opacity: 0.6, flexShrink: 0 }} />
          </Box>
          {/* The refinement, in the status's OWN colour rather than a neutral grey: it is
              a property of the status above it, and a grey second line reads as unrelated
              metadata. `darker` (not `main`) because the pill's ground is `lighter`, and
              that is the pair the palette guarantees contrast for; the opacity is what
              keeps it subordinate to the status word. */}
          <Box
            component="span"
            sx={{
              minWidth: 0,
              textAlign: "start",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 10.5,
              fontWeight: 600,
              lineHeight: 1.25,
              opacity: 0.78
            }}
          >
            {subDefinition.label}
          </Box>
        </>
      ) : (
        <>
          {/* The icon and the caret keep their width — the label is the part that gives.
              A shrinking caret would leave a pill that no longer reads as a dropdown. */}
          <CrmStatusIcon iconKey={status} sx={{ fontSize: 14, flexShrink: 0 }} />
          <Box
            component="span"
            sx={{ mx: 0.25, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {pillText}
          </Box>
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, opacity: 0.6, flexShrink: 0 }} />
        </>
      )}
    </ButtonBase>
  ) : (
    // The 95% case. Dashed, quiet, unmistakably clickable — an invitation, not a defect.
    <ButtonBase
      onClick={open}
      aria-label="הוסף סטטוס"
      sx={{
        height,
        borderRadius: 1,
        px: 0.75,
        border: "1px dashed",
        borderColor: "divider",
        color: "text.secondary",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: "nowrap",
        "&:hover": { borderColor: "primary.main", color: "primary.main" },
        // The 95% case is also the one a realtor taps most, so it gets the same hit area
        // as the filled pill. A 22px dashed outline that only registers a tap on 22px of
        // screen is the empty state failing at the one job it has.
        ...hitAreaSx
      }}
    >
      + סטטוס
    </ButtonBase>
  );

  /**
   * The menu body, as a FLAT ARRAY of direct children.
   *
   * Not a fragment, and not one wrapper <Box> per status: MUI's MenuList reads its own
   * DIRECT children to place initial focus on the selected item and to run arrow-key and
   * type-ahead navigation. A fragment child makes it warn out loud ("The Menu component
   * doesn't accept a Fragment as a child") and a wrapper div makes it silently give up —
   * the menu still looks right and stops being usable from the keyboard.
   */
  const items: ReactNode[] = [
    <MenuItem
      key="hot"
      onClick={(event) => {
        event.stopPropagation();
        onToggleHot();
        // The hot toggle deliberately does NOT close: marking hot and then picking a
        // status is one continuous thought.
      }}
      sx={{ minHeight: isMobile ? 52 : 40 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 1 }}>
        <CrmHotFireIcon isHot={contact.crmIsHot} sx={{ fontSize: 18 }} />
        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600 }}>
          ליד חם
        </Typography>
        <Switch size="small" checked={contact.crmIsHot} readOnly />
      </Stack>
    </MenuItem>,

    <Divider key="hot-divider" sx={{ my: 0.5 }} />,

    ...crmStatuses.flatMap((candidate) => [
      <MenuItem
        key={candidate}
        selected={status === candidate && !subStatus}
        onClick={(event) => choose(event, candidate as CrmStatus, null)}
        sx={{ minHeight: isMobile ? 52 : 40 }}
      >
        <CrmStatusIcon iconKey={candidate} sx={{ fontSize: 16, mr: 1 }} />
        <Typography variant="body2">{crmStatusLabels[candidate as CrmStatus]}</Typography>
      </MenuItem>,

      // Sub-statuses live only under בטיפול, indented, and picking one implies the
      // parent status — one click, not two. The indent has to clear the parent's
      // icon+margin (40px), or the nesting simply does not read: at the original 36px
      // the sub-status label started 4px OUTSIDE its own parent's label.
      ...(candidate === "in_progress"
        ? subDefinitions.map((definition: CrmSubStatusDefinition) => (
            <MenuItem
              key={definition.id}
              selected={subStatus === definition.id}
              onClick={(event) => choose(event, "in_progress", definition.id)}
              sx={{ minHeight: isMobile ? 48 : 36, pl: 7 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {definition.label}
              </Typography>
            </MenuItem>
          ))
        : [])
    ]),

    ...(status
      ? [
          <Divider key="clear-divider" sx={{ my: 0.5 }} />,
          <MenuItem key="clear" onClick={(event) => choose(event, null, null)} sx={{ minHeight: isMobile ? 52 : 40 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              נקה סטטוס
            </Typography>
          </MenuItem>
        ]
      : [])
  ];

  return (
    <>
      {chip}

      {isMobile ? (
        // A bottom drawer, not an anchored menu: an anchored popover near the bottom of
        // a phone screen opens under the thumb and off-viewport.
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={close}
          onClick={(event) => event.stopPropagation()}
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 2, maxHeight: "80vh" } }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="subtitle1" noWrap>
              {contact.name || contact.contactId}
            </Typography>
          </Box>
          <Divider />
          {items}
        </Drawer>
      ) : (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={close}
          onClick={(event) => event.stopPropagation()}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          // 520 is the measured content height (511px: hot + 6 statuses + 7 sub-statuses
          // + 2 dividers). At the previous 460 the LAST status — לא רלוונטי — sat behind
          // an internal scroll with no affordance, on a control whose whole promise is
          // "read and change the status in one click". Popover still clamps this to the
          // viewport on a short screen, so nothing can overflow off-screen.
          PaperProps={{ sx: { minWidth: 220, maxHeight: 520 } }}
        >
          {items}
        </Menu>
      )}
    </>
  );
}
